import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getMessagesBlocksProps } from '../../../../reduxStore/selectors/messages.selector';
import { useRenderMessageItem } from './hooks';
import { HistoryObserver } from './HistoryObserver';
import { BottomObserver } from './BottomObserver';
import { styled } from '@mui/material';

/**
 * Renders the messages list using the legacy `flex-direction: column-reverse` layout.
 *
 * Items are rendered in reversed DOM order so the CSS layout places the newest
 * message at the bottom and keeps the scroll naturally anchored there without
 * any JS intervention.
 *
 * Two sentinel elements manage the conversation bounds:
 * - `BottomObserver` — first DOM child, visually at the bottom. Triggers a
 *   trim of old messages from redux when the user is at the newest end of the
 *   conversation, keeping the in-memory buffer bounded.
 * - `HistoryObserver` — last DOM child, visually at the top. Triggers history
 *   loading when the user scrolls upward past the oldest loaded message.
 */
export const ReversedMessages = styled(
	({ className }: { className?: string }) => {
		const messagesBlockProps = useSelector(getMessagesBlocksProps);
		const renderItem = useRenderMessageItem();

		const children = useMemo(
			() => messagesBlockProps.toReversed().map(renderItem),
			[messagesBlockProps, renderItem],
		);

		return (
			<div className={className}>
				{/* BottomObserver is the first DOM child: in a column-reverse container
				    this places it visually at the very bottom (newest messages side),
				    where it triggers trimming of old messages when visible. */}
				<BottomObserver />
				{children}
				<HistoryObserver />
			</div>
		);
	},
	{ target: 'ReversedMessages-root', label: 'ReversedMessages' },
)({
	display: 'flex',
	flexDirection: 'column-reverse',
	height: '100%',
	width: '100%',
	overflow: 'auto',
});
